import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, HeartHandshake, Sparkles } from "lucide-react";
import { Asterisk, EASE } from "./Reveal";
import { scrollToId } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1501686637-b7aa9c48a882?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxjaGlsZCUyMHNtaWxpbmclMjBwbGF5aW5nJTIwYmxvY2tzfGVufDB8fHx8MTc4NjcxNzQxMXww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      <div className="absolute -top-32 -right-40 h-[34rem] w-[34rem] rounded-full bg-brand-pale/80 blur-3xl" aria-hidden="true" />
      <div className="absolute top-1/2 -left-48 h-96 w-96 rounded-full bg-brand-yellow/30 blur-3xl" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-brand-lightblue/60 bg-white/70 backdrop-blur px-5 py-2 mb-8"
            data-testid="hero-eyebrow"
          >
            <Asterisk className="h-3.5 w-3.5 text-brand-coral" />
            <span className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-brand-blue">
              Pediatric ABA Therapy
            </span>
          </motion.div>

          <h1 className="font-serif font-medium tracking-tight text-brand-ink text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.02]" data-testid="hero-heading">
            <MaskedLine delay={0.15}>The Science</MaskedLine>
            <MaskedLine delay={0.3}>
              of <em className="text-brand-coral">Progress</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-brand-muted"
            data-testid="hero-subcopy"
          >
            Helping children reach meaningful milestones. Individualized, family-centered ABA therapy
            designed to support communication, independence, and meaningful progress across the
            places children live, learn, and grow. We offer daily programs for our pediatric
            clients — and services for teens and adults, too.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-intake"
              onClick={() => scrollToId("intake")}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-coral text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lift hover:bg-[#f06666]"
            >
              Start Intake
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-referral"
              onClick={() => scrollToId("providers")}
              className="inline-flex items-center gap-2 rounded-full border border-brand-blue/30 text-brand-blue font-semibold px-8 py-4 transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 hover:bg-brand-pale hover:border-brand-blue/50"
            >
              Refer a Patient
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="mt-12 flex items-center gap-3 text-sm font-medium text-brand-muted"
            data-testid="hero-settings-line"
          >
            <span>In Home</span>
            <span className="h-1 w-1 rounded-full bg-brand-coral" />
            <span>In School</span>
            <span className="h-1 w-1 rounded-full bg-brand-coral" />
            <span>In Clinic</span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -top-6 -left-6 h-full w-full rounded-t-[999px] rounded-b-[2rem] border-2 border-brand-yellow" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[2rem] shadow-lift">
                <img
                  src={HERO_IMG}
                  alt="Child smiling while playing with colorful blocks"
                  data-testid="hero-image"
                  className="h-[26rem] md:h-[32rem] w-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
              className="absolute -left-4 md:-left-10 top-24 rounded-2xl bg-white/90 backdrop-blur-xl border border-brand-lightblue/40 shadow-lift px-5 py-4 flex items-center gap-3"
              data-testid="hero-badge-family"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-coralpale text-brand-coral">
                <HeartHandshake className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold text-brand-ink leading-tight">
                Family-Centered
                <span className="block text-xs font-medium text-brand-muted">Care, always</span>
              </span>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1 }}
              className="absolute -right-3 md:-right-8 bottom-16 rounded-2xl bg-white/90 backdrop-blur-xl border border-brand-lightblue/40 shadow-lift px-5 py-4 flex items-center gap-3"
              data-testid="hero-badge-bcba"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-pale text-brand-blue">
                <Sparkles className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold text-brand-ink leading-tight">
                BCBA-Supported
                <span className="block text-xs font-medium text-brand-muted">Clinical quality</span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
